import React, { useState } from 'react';
import {
  FDStack,
  FDPrimaryActionButton,
  FDLabelText,
  FDHeadlineText,
  FDBodyText,
  FDClickableText, FDImage, FDIcon, FDDialog, FDDialogTitle, FDDialogContent, FDDialogActions, FDSecondaryActionButton,
  FDTitleText,
} from '@facet-wealth/design';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { dataAttrs } from 'Helpers/Testing';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import useMobileLayout from 'Hooks/useMobileLayout';
import { MoneyMovement } from 'Packages/FacetCore/Experimental/MoneyMovement';
import { followRelationship } from 'Helpers/Affordances';
import { useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { ActionPaper } from './ActionPaper';
import { AllGoalsForAction } from './AllGoalsForAction/AllGoalsForAction';

function truncateText(text, maxLength) {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 3)}...`;
}

const metaOverrides = buildFacetDesignOverride()
  .nameOverride('titlePrefix', {
    _component: FDLabelText,
    _customProps: {
      size: 'lg',
      color: 'textSecondary',
    },
  }, overrideReadOnly)
  .nameOverride('title', {
    _component: FDHeadlineText,
    _customProps: {
      size: 'lg',
    },
  }, overrideReadOnly)
  .nameOverride('subtitle', {
    _component: FDBodyText,
    _customProps: {
      size: 'lg',
    },
  }, overrideReadOnly);

export const Action = ({
  actionResource, completed,
}) => {
  const [showAllGoals, setShowAllGoals] = useState(false);
  const [loading, setLoading] = useState(false);
  const isMobile = useMobileLayout();
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { pageContentResource, setPageContentCollection, refreshPageContentCollection } = usePageContentContext();
  const { displayErrorMessage } = useSnackbarContext();
  const theme = useTheme();
  const [showConfirmCompletedDialog, setShowConfirmCompletedDialog] = useState(false);
  const [loadingInConfirmationDialog, setLoadingInConfirmationDialog] = useState(false);

  const confirmCompleted = async () => {
    setLoadingInConfirmationDialog(true);
    try {
      await followRelationship(actionResource.relationship(fr.relationships.markComplete.name));
      await refreshPageContentCollection();
      setShowConfirmCompletedDialog(false);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setLoadingInConfirmationDialog(false);
    }
  };

  const handleGetStarted = async () => actionResource.withRelationship(fr.relationships.markComplete.name, async () => {
    setShowConfirmCompletedDialog(true);
  }, async () => {
    setLoading(true);
    logStandardizedEvent(categories.activation, eventTypes.clickButton, 'action clicked', {
      eventProperties: {
        accountName: actionResource.attr('title'),
        selectedActionId: actionResource.id(),
      },
    });
    pageContentResource.attr('selectedAction', actionResource.id());
    let nextStepColl;
    try {
      nextStepColl = await pageContentResource.submit();
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setLoading(false);
    }
    if (nextStepColl) {
      setPageContentCollection(nextStepColl);
    }
  });

  let primaryActionLabel = actionResource.attr('primaryActionLabel') || 'Get Started';
  if (isMobile) {
    primaryActionLabel = truncateText(primaryActionLabel, 33);
  }

  const amountItems = actionResource.attr('amountItems').resources().list() || [];

  return (
    <ActionPaper
      {...dataAttrs('scenario-action')}
      completed={completed}
    >
      <FDDialog open={showConfirmCompletedDialog} onClose={() => setShowConfirmCompletedDialog(false)} closeOnBackdropClick>
        <FDDialogTitle value="This is important!" />
        <FDDialogContent>
          <FDStack direction="column" spacing={1}>
            <FDBodyText value="Please confirm that you've made the following change:" />
            <FDTitleText value={actionResource.attr('subtitle')} />
          </FDStack>
        </FDDialogContent>
        <FDDialogActions>
          <FDSecondaryActionButton label="Cancel" onClick={() => setShowConfirmCompletedDialog(false)} />
          <FDPrimaryActionButton loading={loadingInConfirmationDialog} label="Yes, this is complete" onClick={confirmCompleted} />
        </FDDialogActions>
      </FDDialog>
      <FDStack direction={'column'}>
        {/* Title area */}
        <FDStack spacing={2} justifyContent={'space-between'}>
          <FDStack spacing={1} direction={'column'}>
            <MetaControl resource={actionResource} attrName={'titlePrefix'} metaOverrideSet={metaOverrides} />
            <FDStack direction={'row'} spacing={1} wrap>
              <MetaControl resource={actionResource} attrName={'title'} metaOverrideSet={metaOverrides} />
              {actionResource.withRelationship(fr.relationships.additionalOutcomes.name,
                (rel) => (
                  <FDHeadlineText value={<FDClickableText appearance={'standalone'} label={rel.meta('prompt')} onClick={() => setShowAllGoals(true)} />} size={'lg'} />
                ),
                () => null)}
            </FDStack>
          </FDStack>
          {
            completed
              ? <FDIcon icon="checkCircleIcon" color="mint500" size="lg" />
              : actionResource.withRelationship(fr.relationships.image.name, (rel) => {
                return (<FDImage alt={''} src={rel.link('related').url()} width={'64px'} height={'64px'} rounded={false} />);
              }, () => null)
          }
        </FDStack>
        <AllGoalsForAction actionResource={actionResource} setShowAllGoals={setShowAllGoals} showAllGoals={showAllGoals} />
        <FDStack direction={'column'} spacing={4}>
          <FDStack>
            <MetaControl resource={actionResource} attrName={'subtitle'} metaOverrideSet={metaOverrides} />
          </FDStack>
          <FDStack spacing={1} justifyContent={'space-between'}>
            {amountItems.length > 0 && <MoneyMovement resource={actionResource} backgroundColor={completed ? theme.palette.facet.lightGray : theme.palette.common.white} />}
          </FDStack>
          {completed ? null : (
            <FDStack direction={'column'} spacing={2}>
              <FDPrimaryActionButton
                loading={loading}
                isFullWidth
                disabled={!actionResource.meta().canPut()}
                label={primaryActionLabel}
                onClick={() => { return handleGetStarted(); }}
                {...dataAttrs('account-action-button')}
              />
            </FDStack>
          )}
        </FDStack>
      </FDStack>
    </ActionPaper>
  );
};

Action.propTypes = {
  actionResource: PropTypes.instanceOf(fr.Resource).isRequired,
};
